import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Auth } from '@freelancer/auth';
import { CrossDomainSSODomainsGetResultAjax } from '@freelancer/auth/auth.interface';
import { ResponseData } from '@freelancer/datastore';
import { Facebook } from '@freelancer/facebook';
import { FreelancerHttp } from '@freelancer/freelancer-http';
import { Location } from '@freelancer/location';
import { ErrorCodeApi } from 'api-typings/errors/errors';
import { catchError, map, take } from 'rxjs/operators';
import {
  AuthResponseAjax,
  AuthSuccessResponseAjax,
  LoginError,
  RedirectUrlGetResultAjax,
  UserCheckError,
} from './login-signup.backend-model';
import { AuthResponse } from './login-signup.model';
import {
  transformAuthResponse,
  transformResponseData,
} from './login-signup.transformers';

interface RedirectParams {
  next?: string;
  goto?: string;
  redirectDisabled?: boolean;
  isModal?: boolean;
  projectCollaborationInvitation?: string;
  contestCollaborationInvitation?: string;
}

/**
 * Service which provides an interface to backend endpoints for the login/signup funnel
 */
@Injectable({
  providedIn: 'root',
})
export class LoginSignupService {
  constructor(
    private auth: Auth,
    private rawHttp: HttpClient,
    private freelancerHttp: FreelancerHttp,
    private router: Router,
    private facebook: Facebook,
    private location: Location,
  ) {}

  login(
    user: string,
    password: string,
  ): Promise<ResponseData<AuthResponse, LoginError>> {
    return this.auth.deviceToken$
      .pipe(take(1))
      .toPromise()
      .then(deviceToken => {
        // return a custom error because passing undefined
        // to the backend will result in `InvalidInput`
        if (!deviceToken) {
          return {
            status: 'error',
            errorCode: ErrorCodeApi.AUTH_DEVICE_TOKEN_INVALID,
          };
        }
        return this.freelancerHttp
          .post<AuthResponseAjax, LoginError>(
            'auth/login.php',
            {
              user,
              password,
              device_token: deviceToken,
            },
            {
              isGaf: true,
              serializeBody: true,
            },
          )
          .pipe(
            take(1),
            map(responseData =>
              transformResponseData(responseData, transformAuthResponse),
            ),
          )
          .toPromise();
      });
  }

  verify(
    preLoginToken: string,
    password: string,
  ): Promise<ResponseData<AuthSuccessResponseAjax, LoginError>> {
    return this.auth.deviceToken$
      .pipe(take(1))
      .toPromise()
      .then(deviceToken => {
        if (!deviceToken) {
          return {
            status: 'error',
            errorCode: ErrorCodeApi.AUTH_DEVICE_TOKEN_INVALID,
          };
        }
        return this.freelancerHttp
          .post<AuthSuccessResponseAjax, LoginError>(
            'auth/verify2FALogin.php',
            {
              totp: password,
              device_token: deviceToken,
              pre_login_token: preLoginToken,
            },
            {
              isGaf: true,
              serializeBody: true,
            },
          )
          .pipe(take(1))
          .toPromise();
      });
  }

  facebookLogin(): Promise<ResponseData<AuthSuccessResponseAjax, LoginError>> {
    return this.facebook.login().then(response => {
      if (response.status !== 'success') {
        return {
          status: 'error',
          errorCode: ErrorCodeApi.AUTH_FACEBOOK_LINK_FAILED,
        } as const;
      }

      return this.freelancerHttp
        .post<AuthSuccessResponseAjax, LoginError>(
          'auth/facebookLogin.php',
          {
            app_id: this.facebook.appId,
            credentials: response.result.signedRequest,
            access_token: response.result.accessToken,
          },
          {
            isGaf: true,
            serializeBody: true,
          },
        )
        .pipe(take(1))
        .toPromise();
    });
  }

  checkUserDetails(
    user: {
      username?: string;
      email?: string;
    },
    password?: string,
  ): Promise<ResponseData<undefined, UserCheckError>> {
    return this.freelancerHttp
      .post<undefined, UserCheckError>('users/0.1/users/check', {
        user,
        password,
      })
      .pipe(take(1))
      .toPromise();
  }

  /**
   * Handle successful authentication, be it on login or signup
   */
  handleSuccess(
    action: 'login' | 'signup',
    userId: number,
    authToken: string,
    rememberLogin: boolean,
    redirectParams?: RedirectParams,
  ) {
    // set auth session (automatically sets cookies)
    this.auth.setSession(userId.toString(), authToken);

    const crossDomainSSOPromise = this.setCrossDomainSSO(
      userId,
      authToken,
      rememberLogin,
    );

    const redirectPromise =
      action === 'login'
        ? this.getLoginRedirectUrl(redirectParams)
        : // TODO: get signup URL
          Promise.resolve('/new-freelancer');

    // wait for both reqest chains to finish, then redirect
    Promise.all([redirectPromise, crossDomainSSOPromise]).then(
      ([redirectUrl]) => {
        if (redirectUrl === 'reload') {
          // TODO: reload?
        }
        if (redirectUrl.startsWith('http')) {
          // absolute URL: do a hard navigate
          return this.location.redirect(redirectUrl);
        }
        this.router.navigate([redirectUrl]);
      },
    );
  }

  /**
   * Logs a user in on other domains that they should be logged in on.
   * Fetches domains from `getCrossDomainSSODomains.php`, then sends requests
   * to those domains to set cookies on them.
   */
  setCrossDomainSSO(
    userId: number,
    authToken: string,
    rememberLogin: boolean,
  ): Promise<void> {
    // this is equivalent to the `serializeBody` option for freelancerHttp
    // needs to be manually done since we're using raw httpClient for the other domains
    const formBody = this.freelancerHttp._serialize({
      userId,
      authToken,
      rememberLogin,
    });

    const headers = new HttpHeaders().append(
      'Content-Type',
      'application/x-www-form-urlencoded',
    );

    return this.freelancerHttp
      .get<CrossDomainSSODomainsGetResultAjax>(
        'auth/getCrossDomainSSODomains.php',
        { isGaf: true },
      )
      .pipe(take(1))
      .toPromise()
      .then(response => {
        if (response.status === 'error') {
          return;
        }
        return Promise.all(
          response.result.domains.map(domain =>
            this.rawHttp
              .post(`${domain}/ajax-api/auth/crossDomainSSO.php`, formBody, {
                headers,
                withCredentials: true, // send and receive cookies
              })
              .pipe(
                // silence any errors
                catchError(_ => Promise.resolve()),
                take(1),
              )
              .toPromise(),
          ),
        ).then();
      });
  }

  /**
   * Returns the next route to load after logging in
   */
  getLoginRedirectUrl({
    isModal = false,
    redirectDisabled = false,
    next,
    goto,
    projectCollaborationInvitation,
    contestCollaborationInvitation,
  }: RedirectParams = {}): Promise<string> {
    return this.freelancerHttp
      .post<RedirectUrlGetResultAjax>(
        'auth/getLoginRedirectUrl.php',
        {
          is_modal: isModal.toString(),
          redirect_disabled: redirectDisabled.toString(),
          next,
          goto,
          project_collaboration_invitation: projectCollaborationInvitation,
          contest_collaboration_invitation: contestCollaborationInvitation,
        },
        { isGaf: true, serializeBody: true },
      )
      .pipe(take(1))
      .toPromise()
      .then(response =>
        response.status === 'success'
          ? response.result.redirect_url
          : // default to dashboard for login
            'dashboard',
      );
  }
}
